import { useState, useCallback, useEffect, useRef } from "react";
import { useParams, useOutletContext, Link } from "react-router-dom";
import { Button, Skeleton, Switch, notification } from "antd";
import { Save, RotateCcw, ArrowLeft, Workflow, List, PanelRightOpen } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getEnvironmentService } from "../../backend";
import type { UpdatePreviewConfigRequest } from "@microtica/ms-engine-sdk";
import PreviewWorkflowCanvas from "./components/PreviewWorkflowCanvas";
import PreviewNodeConfigPanel from "./components/PreviewNodeConfigPanel";
import PreviewPRList from "./components/PreviewPRList";
import { IEnvDetails } from "../../types";
import { useKubernetesClusters } from "../../hooks/queries/environments";
import { useProjectApps } from "../../hooks/queries/preview-environments";
import type { PreviewConfigFormState } from "../../types/preview-environments";
import type { PreviewWithGitMetadata } from "../../types/preview-environments";
import { validateNamespacePattern, safeJsonParse, fetchDeployedApps } from "../../utils/preview-utils";
import {
    DEFAULT_TARGET_BRANCH,
    DEFAULT_NAMING_PATTERN,
    DEFAULT_MAX_CONCURRENT_PREVIEWS,
    DEFAULT_TRIGGER_EVENTS,
    PREVIEWS_REFRESH_INTERVAL,
    PREVIEWS_STALE_TIME,
    DRAFT_AUTOSAVE_DELAY,
    PANEL_MAX_WIDTH,
    PANEL_MIN_WIDTH,
    PANEL_COLLAPSE_THRESHOLD,
    PANEL_DEFAULT_WIDTH,
} from "../../utils/preview-constants";

export default function EnvironmentPreviewPlayground() {
    const { projectId, envId } = useParams() as { projectId: string; envId: string };
    const queryClient = useQueryClient();
    const { env } = useOutletContext<{ env: IEnvDetails }>();

    const [viewMode, setViewMode] = useState<'workflow' | 'previews'>('workflow');
    const [formState, setFormState] = useState<PreviewConfigFormState | null>(null);
    const [isDirty, setIsDirty] = useState(false);
    const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
    const [panelWidth, setPanelWidth] = useState(PANEL_DEFAULT_WIDTH);
    const isResizingRef = useRef(false);
    const panelCollapsed = panelWidth === 0;

    const handleResizeStart = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        isResizingRef.current = true;
        const startX = e.clientX;
        const startWidth = panelWidth;

        const handleMouseMove = (moveEvent: MouseEvent) => {
            if (!isResizingRef.current) return;
            const delta = startX - moveEvent.clientX;
            const newWidth = Math.max(0, Math.min(PANEL_MAX_WIDTH, startWidth + delta));
            if (newWidth < PANEL_COLLAPSE_THRESHOLD) {
                setPanelWidth(0);
            } else {
                setPanelWidth(Math.max(PANEL_MIN_WIDTH, newWidth));
            }
        };

        const handleMouseUp = () => {
            isResizingRef.current = false;
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [panelWidth]);

    const { data: clusters } = useKubernetesClusters(projectId, envId);

    // Fetch all project apps (microservices)
    const { data: discoveredApps } = useProjectApps(projectId);

    // Sync discovered apps with form state (silent — not a user change)
    useEffect(() => {
        if (discoveredApps && discoveredApps.length > 0 && formState) {
            const existingAppMap = new Map(formState.apps.map(a => [a.appName, a]));
            const discoveredNames = new Set(discoveredApps.map(a => a.name));

            // Merge: discovered apps + any saved apps not in discovered list (preserves config)
            const syncedApps = [
                ...discoveredApps.map(app => ({
                    appName: app.name,
                    included: existingAppMap.get(app.name)?.included ?? false,
                    pipelineId: existingAppMap.get(app.name)?.pipelineId,
                    envVarOverrides: existingAppMap.get(app.name)?.envVarOverrides || [],
                    filePattern: existingAppMap.get(app.name)?.filePattern
                })),
                // Keep saved apps that aren't in the discovered list (may have been deleted but still configured)
                ...formState.apps.filter(a => !discoveredNames.has(a.appName))
            ];

            const hasChanged = JSON.stringify(syncedApps) !== JSON.stringify(formState.apps);
            if (hasChanged) {
                setFormState(prev => prev ? { ...prev, apps: syncedApps } : null);
            }
        }
    }, [discoveredApps, formState?.apps.length]);

    // Load preview configuration
    const {
        data: configData,
        isLoading: configLoading,
    } = useQuery({
        queryKey: ['previewConfig', envId, projectId],
        queryFn: async () => {
            const response = await getEnvironmentService().getPreviewConfig(envId, projectId);
            return response.data;
        },
        staleTime: 30000,
    });

    // Load preview environments list
    const {
        data: previewsData,
        isLoading: previewsLoading,
        refetch: refetchPreviews
    } = useQuery({
        queryKey: ['previews', envId, projectId],
        queryFn: async () => {
            const response = await getEnvironmentService().listPreviews(envId, projectId);
            const items = (response.data.previews || []) as PreviewWithGitMetadata[];
            items.sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime());
            return items;
        },
        refetchInterval: PREVIEWS_REFRESH_INTERVAL,
        staleTime: PREVIEWS_STALE_TIME,
    });

    // Initialize form state from loaded config
    useEffect(() => {
        if (configData && !formState) {
            setFormState({
                enabled: Boolean(configData.enabled),
                targetBranch: configData.targetBranch || DEFAULT_TARGET_BRANCH,
                branchPattern: configData.branchPattern || "",
                tagPattern: configData.tagPattern || "",
                triggerEvents: Array.isArray(configData.triggerEvents) ? configData.triggerEvents : DEFAULT_TRIGGER_EVENTS,
                namingPattern: configData.namingPattern || DEFAULT_NAMING_PATTERN,
                rootClusterId: configData.rootClusterId || undefined,
                autoDestroyOnPRClose: configData.autoDestroyOnPRClose !== false,
                maxConcurrentPreviews: configData.maxConcurrentPreviews || DEFAULT_MAX_CONCURRENT_PREVIEWS,
                postCommentToPR: configData.postCommentToPR !== false,
                apps: (configData.apps || []).map(a => ({
                    appName: a.appName!,
                    included: Boolean(a.included ?? true),
                    pipelineId: (a as any).pipelineId || undefined,
                    envVarOverrides: a.envVarOverrides
                        ? Object.entries(a.envVarOverrides).map(([key, value]) => ({ key, value }))
                        : [],
                    filePattern: (a as any).filePattern || undefined
                }))
            });
        }
    }, [configData, formState]);

    // Save configuration mutation
    const saveMutation = useMutation({
        mutationFn: async (config: PreviewConfigFormState) => {
            const updateRequest: UpdatePreviewConfigRequest = {
                enabled: Boolean(config.enabled),
                targetBranch: config.targetBranch,
                branchPattern: config.branchPattern || undefined,
                tagPattern: config.tagPattern || undefined,
                triggerEvents: (config.triggerEvents && config.triggerEvents.length > 0) ? config.triggerEvents : DEFAULT_TRIGGER_EVENTS,
                namingPattern: config.namingPattern,
                rootClusterId: config.rootClusterId || "",
                autoDestroyOnPRClose: Boolean(config.autoDestroyOnPRClose),
                maxConcurrentPreviews: config.maxConcurrentPreviews,
                postCommentToPR: Boolean(config.postCommentToPR),
                apps: await (async () => {
                    const appsToSave = config.apps.filter(
                        a => a.included || (a.envVarOverrides && a.envVarOverrides.length > 0) || a.filePattern
                    );
                    const namesToResolve = appsToSave
                        .filter(a => a.included && !a.pipelineId)
                        .map(a => a.appName);
                    const resolvedMap = new Map<string, string | undefined>();
                    if (namesToResolve.length > 0) {
                        const deployed = await fetchDeployedApps(projectId, envId, namesToResolve);
                        for (const d of deployed) {
                            resolvedMap.set(d.name, d.pipelineId);
                        }
                    }
                    return appsToSave.map(a => ({
                        appName: a.appName,
                        included: Boolean(a.included),
                        pipelineId: resolvedMap.get(a.appName) ?? a.pipelineId,
                        envVarOverrides: (a.envVarOverrides || []).reduce(
                            (acc, { key, value }) => ({ ...acc, [key]: value }),
                            {} as Record<string, string>
                        ),
                        filePattern: a.filePattern || undefined
                    }));
                })() as any
            };
            await getEnvironmentService().updatePreviewConfig(envId, projectId, updateRequest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['previewConfig', envId, projectId] });
            setIsDirty(false);
            notification.success({
                message: 'Configuration saved successfully',
                placement: 'topRight',
                duration: 3,
            });
        },
        onError: (error: any) => {
            notification.error({
                message: 'Failed to save configuration',
                description: error?.response?.data?.message || error.message,
                placement: 'topRight',
                duration: 5,
            });
        },
    });

    // Auto-save draft to localStorage
    useEffect(() => {
        if (formState && isDirty) {
            const timer = setTimeout(() => {
                localStorage.setItem(
                    `preview-playground-draft-${envId}`,
                    JSON.stringify(formState)
                );
            }, DRAFT_AUTOSAVE_DELAY);
            return () => clearTimeout(timer);
        }
    }, [formState, isDirty, envId]);

    // Restore draft on mount
    useEffect(() => {
        const draft = localStorage.getItem(`preview-playground-draft-${envId}`);
        if (draft && !formState) {
            const parsed = safeJsonParse<PreviewConfigFormState>(draft);
            if (parsed) {
                setFormState(parsed);
                setIsDirty(true);
            }
        }
    }, [envId, formState]);

    // Warn on navigate away if unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    const handleSave = useCallback(() => {
        if (formState) {
            saveMutation.mutate(formState);
            localStorage.removeItem(`preview-playground-draft-${envId}`);
        }
    }, [formState, saveMutation, envId]);

    const handleReset = useCallback(() => {
        if (configData) {
            setFormState({
                enabled: Boolean(configData.enabled),
                targetBranch: configData.targetBranch || DEFAULT_TARGET_BRANCH,
                branchPattern: configData.branchPattern || "",
                namingPattern: configData.namingPattern || DEFAULT_NAMING_PATTERN,
                rootClusterId: configData.rootClusterId || undefined,
                autoDestroyOnPRClose: configData.autoDestroyOnPRClose !== false,
                maxConcurrentPreviews: configData.maxConcurrentPreviews || DEFAULT_MAX_CONCURRENT_PREVIEWS,
                postCommentToPR: configData.postCommentToPR !== false,
                apps: (configData.apps || []).map(a => ({
                    appName: a.appName!,
                    included: Boolean(a.included ?? true),
                    pipelineId: (a as any).pipelineId || undefined,
                    envVarOverrides: a.envVarOverrides
                        ? Object.entries(a.envVarOverrides).map(([key, value]) => ({ key, value }))
                        : [],
                    filePattern: (a as any).filePattern || undefined
                }))
            });
            setIsDirty(false);
            localStorage.removeItem(`preview-playground-draft-${envId}`);
        }
    }, [configData, envId]);

    const updateFormState = useCallback((updates: Partial<PreviewConfigFormState>) => {
        setFormState(prev => prev ? { ...prev, ...updates } : null);
        setIsDirty(true);
    }, []);

    if (configLoading || !formState) {
        return (
            <div style={{ height: 'calc(100vh - 56px - 120px)', padding: 24 }}>
                <Skeleton active paragraph={{ rows: 10 }} />
            </div>
        );
    }

    return (
        <div style={{ margin: 0, padding: 0, width: '100%', height: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Dedicated Header */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: "#ffffff",
                borderBottom: "1px solid #f0f0f0",
                flexShrink: 0,
                padding: "28px 50px 0 50px"
            }}>
                {/* Top row: back + title + actions */}
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "32px",
                    paddingBottom: "28px"
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "24px", flex: 1 }}>
                        <Link to={`/projects/${projectId}/environments/${envId}`}>
                            <Button
                                icon={<ArrowLeft size={16} />}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}
                            />
                        </Link>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <span style={{ fontSize: "20px", fontWeight: 600, color: "#111827", margin: "0", letterSpacing: "-0.02em", display: "flex", alignItems: "center", gap: "8px" }}>
                                Preview Playground
                                <span style={{
                                    fontSize: "11px",
                                    fontWeight: 600,
                                    color: "#7c3aed",
                                    background: "#ede9fe",
                                    padding: "2px 8px",
                                    borderRadius: "4px",
                                    letterSpacing: "0.02em",
                                    lineHeight: "18px",
                                    textTransform: "uppercase",
                                }}>
                                    Beta
                                </span>
                            </span>
                            <span style={{ fontSize: "13px", fontWeight: 400, marginTop: "3px", color: "#9ca3af" }}>
                                {env?.name}
                            </span>
                        </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "8px", height: 32 }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            height: 32,
                            padding: '0 12px',
                            borderRadius: 6,
                            border: '1px solid #d9d9d9',
                            background: '#fff',
                        }}>
                            <span style={{
                                fontSize: 13,
                                fontWeight: 500,
                                color: formState?.enabled ? '#059669' : '#6b7280',
                                lineHeight: '32px',
                            }}>
                                {formState?.enabled ? 'Active' : 'Paused'}
                            </span>
                            <Switch
                                size="small"
                                checked={formState?.enabled}
                                onChange={(enabled) => updateFormState({ enabled })}
                            />
                        </div>
                        <Button
                            icon={<RotateCcw size={14} />}
                            onClick={handleReset}
                            disabled={saveMutation.isPending || !isDirty || viewMode !== 'workflow'}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: 32,
                                opacity: (isDirty && viewMode === 'workflow') ? 1 : 0.5
                            }}
                        />
                        <Button
                            type={isDirty ? "primary" : "default"}
                            icon={<Save size={14} />}
                            onClick={handleSave}
                            loading={saveMutation.isPending}
                            disabled={!isDirty || viewMode !== 'workflow' || !!validateNamespacePattern(formState?.namingPattern ?? "")}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                fontWeight: 500,
                                height: 32,
                                position: 'relative',
                                opacity: viewMode === 'workflow' ? 1 : 0.5
                            }}
                        >
                            Save
                            {isDirty && viewMode === 'workflow' && (
                                <span style={{
                                    position: 'absolute',
                                    top: -4,
                                    right: -4,
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    background: '#f59e0b',
                                    border: '2px solid #fff'
                                }} />
                            )}
                        </Button>
                    </div>
                </div>

            </div>

            {/* Main Content Area */}
            <div style={{
                flex: 1,
                display: 'flex',
                overflow: 'hidden',
                position: 'relative'
            }}>
                {/* Floating view switcher */}
                <div style={{
                    position: 'absolute',
                    top: 16,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 10,
                    display: 'flex',
                    background: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: 10,
                    padding: 3,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    gap: 2
                }}>
                    {([
                        { key: 'workflow', label: 'Workflow', icon: <Workflow size={13} /> },
                        { key: 'previews', label: 'Previews', icon: <List size={13} /> },
                    ] as const).map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setViewMode(tab.key)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "6px 16px",
                                border: "none",
                                borderRadius: 7,
                                background: viewMode === tab.key ? "#111827" : "transparent",
                                cursor: "pointer",
                                fontSize: "13px",
                                fontWeight: viewMode === tab.key ? 600 : 400,
                                color: viewMode === tab.key ? "#ffffff" : "#6b7280",
                                transition: "all 0.15s ease",
                                outline: "none",
                                letterSpacing: viewMode === tab.key ? "-0.01em" : "0"
                            }}
                            onMouseEnter={e => {
                                if (viewMode !== tab.key) {
                                    e.currentTarget.style.background = "#f3f4f6";
                                    e.currentTarget.style.color = "#374151";
                                }
                            }}
                            onMouseLeave={e => {
                                if (viewMode !== tab.key) {
                                    e.currentTarget.style.background = "transparent";
                                    e.currentTarget.style.color = "#6b7280";
                                }
                            }}
                        >
                            <span style={{ display: "flex", alignItems: "center" }}>
                                {tab.icon}
                            </span>
                            {tab.label}
                        </button>
                    ))}
                </div>
                {/* Workflow View */}
                {viewMode === 'workflow' && (
                    <>
                        {/* Workflow Canvas */}
                        <div style={{ flex: 1, position: 'relative', background: '#fafbfc' }}>
                            <PreviewWorkflowCanvas
                                formState={formState}
                                updateFormState={updateFormState}
                                clusters={clusters}
                                selectedNodeId={selectedNodeId}
                                onNodeSelect={setSelectedNodeId}
                            />
                        </div>

                        {/* Right Panel - Node Configuration */}
                        <div style={{ position: 'relative', display: 'flex', flexShrink: 0 }}>
                            {panelCollapsed ? (
                                <button
                                    onClick={() => setPanelWidth(PANEL_DEFAULT_WIDTH)}
                                    style={{
                                        position: 'absolute',
                                        left: -14,
                                        top: 20,
                                        zIndex: 10,
                                        width: 28,
                                        height: 28,
                                        borderRadius: '50%',
                                        border: '1px solid #e5e7eb',
                                        background: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                        padding: 0,
                                    }}
                                    title="Expand panel"
                                >
                                    <PanelRightOpen size={14} style={{ color: '#6b7280' }} />
                                </button>
                            ) : (
                                <div
                                    onMouseDown={handleResizeStart}
                                    style={{
                                        position: 'absolute',
                                        left: -3,
                                        top: 0,
                                        bottom: 0,
                                        width: 6,
                                        cursor: 'col-resize',
                                        zIndex: 10,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <div style={{
                                        width: 3,
                                        height: 32,
                                        borderRadius: 2,
                                        background: '#d1d5db',
                                        transition: 'background 0.15s',
                                    }} />
                                </div>
                            )}
                            <div style={{
                                width: panelWidth,
                                overflow: 'hidden',
                                borderLeft: panelCollapsed ? 'none' : '1px solid #f0f0f0',
                                background: '#ffffff',
                                transition: isResizingRef.current ? 'none' : 'width 0.2s ease',
                            }}>
                                <div style={{
                                    width: panelWidth || PANEL_DEFAULT_WIDTH,
                                    minWidth: PANEL_MIN_WIDTH,
                                    padding: '20px',
                                    overflowY: 'auto',
                                    height: '100%',
                                }}>
                                    <PreviewNodeConfigPanel
                                        selectedNodeId={selectedNodeId}
                                        formState={formState}
                                        updateFormState={updateFormState}
                                        clusters={clusters}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* Previews View */}
                {viewMode === 'previews' && (
                    <div style={{
                        flex: 1,
                        overflow: 'auto',
                        padding: '64px 50px 50px'
                    }}>
                        <PreviewPRList
                            projectId={projectId}
                            envId={envId}
                            previews={previewsData || []}
                            loading={previewsLoading}
                            onRefresh={refetchPreviews}
                            clusters={clusters}
                        />
                    </div>
                )}
            </div>

        </div>
    );
}
